import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { clsx } from "clsx";
import { ChevronDown, LogOut, Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthStore } from "@/auth/store";
import { useLogout, useUser } from "@/hooks/useAuth";

export function UserDropdown() {
  const user = useUser();
  const logout = useLogout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        close();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, close]);

  function handleLogout() {
    logout.mutate(undefined, {
      onError: () => {
        // Clear local state even if API call fails
        useAuthStore.getState().clearAuth();
        queryClient.clear();
        void navigate({ to: "/login" });
      },
    });
  }

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}` : "?";

  return (
    <div ref={dropdownRef} className="relative">
      {/* Avatar trigger */}
      <button
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        className={clsx(
          "flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1",
          "transition-colors duration-150",
          isOpen ? "bg-muted" : "hover:bg-subtle",
        )}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-adara-50 text-[11px] font-semibold text-adara-700 dark:bg-adara-900 dark:text-adara-200">
          {initials}
        </div>
        <span className="hidden text-sm font-medium text-sub md:block">
          {user?.firstName}
        </span>
        <ChevronDown
          size={14}
          className={clsx(
            "hidden text-faint transition-transform duration-200 md:block",
            isOpen && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-edge bg-panel shadow-lg animate-fade-in">
          {/* User info header */}
          <div className="border-b border-edge-soft px-4 py-3">
            <p
              className="truncate text-sm font-semibold text-main"
              title={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
            >
              {user?.firstName} {user?.lastName}
            </p>
            <p
              className="mt-0.5 break-all text-xs text-dim sm:truncate"
              title={user?.email}
            >
              {user?.email}
            </p>
          </div>

          {/* Menu items */}
          <div className="p-1.5">
            <button
              type="button"
              onClick={() => {
                close();
                void navigate({ to: "/settings", search: { tab: "profile" } });
              }}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-sub transition-colors hover:bg-subtle"
            >
              <Settings size={16} className="text-faint" aria-hidden="true" />
              Ustawienia
            </button>

            <div className="my-1 border-t border-edge-soft" />

            <button
              type="button"
              onClick={handleLogout}
              disabled={logout.isPending}
              className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              <LogOut size={16} className="text-red-400" aria-hidden="true" />
              Wyloguj się
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
